import React, { useRef, useState, useCallback } from 'react';
import './PhotoUpload.css';

interface PhotoUploadProps {
  currentAvatar?: string;
  onPhotoChange: (photoUrl: string) => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  currentAvatar = '😊',
  onPhotoChange,
  size = 'medium',
  className = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = useCallback((file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    // Convert to base64 for local storage/preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        onPhotoChange(result);
      }
      setIsUploading(false);
    };
    reader.onerror = () => {
      alert('Error reading file');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  }, [onPhotoChange]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const isEmoji = currentAvatar && currentAvatar.length <= 2 && /\p{Emoji}/u.test(currentAvatar);
  const isDataUrl = currentAvatar && currentAvatar.startsWith('data:');
  const isUrl = currentAvatar && (currentAvatar.startsWith('http') || currentAvatar.startsWith('https'));

  return (
    <div className={`photo-upload ${size} ${className}`}>
      <div
        className={`photo-upload-container ${dragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        
        <div className="photo-preview">
          {isEmoji ? (
            <div className="emoji-avatar">{currentAvatar}</div>
          ) : (isDataUrl || isUrl) ? (
            <img src={currentAvatar} alt="Avatar" className="uploaded-photo" />
          ) : (
            <div className="emoji-avatar">😊</div>
          )}
        </div>

        <div className="photo-upload-overlay">
          {isUploading ? (
            <div className="upload-spinner">
              <div className="spinner"></div>
              <span>Uploading...</span>
            </div>
          ) : (
            <div className="upload-prompt">
              <div className="camera-icon">📷</div>
              <span>Change Photo</span>
            </div>
          )}
        </div>
      </div>

      <div className="photo-upload-info">
        <p>Click or drag to upload</p>
        <p>Max 5MB • JPG, PNG, GIF</p>
      </div>
    </div>
  );
};

export default PhotoUpload;
